/**
 * Utility functions for preview components
 */

/** True when string looks like HTML from the rich-text editor (e.g. <p>...</p>), not plain text */
export function isProbablyHtmlString(str) {
  if (typeof str !== "string") return false;
  const t = str.trim();
  return t.length > 0 && /^<[a-zA-Z!/?]/.test(t) && t.includes(">");
}

/**
 * Prepare short description / rich text body for {@link RenderHtml}: strip Lexical `editor-shell`
 * wrapper, escape and wrap plain text when needed.
 */
export function normalizeRichTextFieldForRenderHtml(raw) {
  if (raw == null || raw === "") return "";
  let s = String(raw);
  if (s.includes('class="editor-shell"')) {
    s = s.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1");
  }
  if (isProbablyHtmlString(s)) return s;
  const escaped = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p style="white-space: pre-wrap; margin: 0">${escaped}</p>`;
}

function looksLikeUuid(str) {
  if (typeof str !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    str.trim(),
  );
}

/**
 * Prefer a human-readable category label; APIs often put the UUID in `rich_text_category`.
 * Document view ({@link Documents/components/Nodes/RichTextNode}) uses `detail.category_info.category_name`.
 */
function pickRichTextCategoryLabel(...candidates) {
  const trimmed = [];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const t = String(c).trim();
    if (!t) continue;
    trimmed.push(t);
  }
  const readable = trimmed.find((t) => !looksLikeUuid(t));
  return readable ?? trimmed[0] ?? "";
}

/**
 * Human-readable rich-text category for audit/preview (matches shapes from live nodes + audit API).
 */
export function getRichTextCategoryDisplayValue(source) {
  if (source == null || typeof source !== "object") return "";
  const pickString = (v) =>
    typeof v === "string" && v.trim() ? v.trim() : "";

  const categoryInfo =
    (source.category_info && typeof source.category_info === "object"
      ? source.category_info
      : null) ||
    (source.detail?.category_info && typeof source.detail.category_info === "object"
      ? source.detail.category_info
      : null);

  const categoryInfoName = categoryInfo
    ? pickString(categoryInfo.category_name) || pickString(categoryInfo.name)
    : "";

  const nested =
    source.rich_text_category_info ||
    source.category ||
    source.type_info ||
    null;
  let nestedStr = "";
  if (nested && typeof nested === "object") {
    nestedStr =
      pickString(nested.name) ||
      pickString(nested.type_name) ||
      pickString(nested.label) ||
      pickString(nested.title);
  }

  return pickRichTextCategoryLabel(
    categoryInfoName,
    pickString(source.category_name),
    pickString(source.richTextCategory),
    pickString(source.categoryName),
    nestedStr,
    pickString(source.rich_text_category),
    typeof source.rich_text_category_ref === "string"
      ? source.rich_text_category_ref.trim()
      : "",
  );
}

/**
 * Step heading uses {@link getStepDisplayOrdinal} (tree rank / sibling_order), but stored
 * short_description often still begins with an older "Step N" line. Rewrite the first lead
 * "Step N" token so it matches the heading when it appears near the start of the field.
 */
export function alignStepShortDescriptionOrdinalWithHeading(
  raw,
  displayStepNumber,
) {
  const n = Number(displayStepNumber);
  if (!Number.isFinite(n) || n < 1 || raw == null || raw === "") return raw;
  const s = String(raw);
  const re = /\bStep\s+(\d+)\b/;
  const m = re.exec(s);
  if (!m) return raw;
  const idx = m.index;
  if (idx > 200) return raw;
  const old = parseInt(m[1], 10);
  if (old === n) return raw;
  return s.replace(re, `Step ${n}`);
}

/**
 * Audit / snapshot child nodes (video, canvas, …) often embed a human "tree path" inside
 * `short_description` (e.g. "Rev A- > Section 2 > Step 2 > Substep 2B > Video 2") while the
 * computed Section/Step/Substep from the tree can disagree. When present, prefer this path
 * for the grey breadcrumb in RevisionPreview {@link ./MoveAction/RevisionPreview/Step/RenderNodes.js}.
 * Returns a plain string with " > " separators, or null.
 */
export function extractAuditEmbeddedTreePathFromShortDescription(raw) {
  if (raw == null || raw === "") return null;
  const plain = String(raw)
    .replace(/<[^>]*>/g, " ")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return null;
  const withSub = plain.match(
    /\bSection\s+\d+\s*>\s*Step\s+\d+\s*>\s*Substep\s+\d+[A-Za-z]\b/i,
  );
  if (withSub) return withSub[0].replace(/\s*>\s*/g, " > ").trim();
  const noSub = plain.match(/\bSection\s+\d+\s*>\s*Step\s+\d+\b/i);
  if (noSub) return noSub[0].replace(/\s*>\s*/g, " > ").trim();
  return null;
}

/**
 * Get node indices for a specific content type
 * @param {Array} data - Array of sections with steps and children
 * @param {string} type - Content type to index (e.g., "rich-text", "video", "canvas")
 * @returns {Array} Array of indices with sectionIdx, stepIdx, childIdx, substepIdx (optional), and globalIndex
 */
export function getNodeIndices(data, type) {
  let indices = [];
  let currentIndex = 1;
  const childMatchesType = (child, t) => {
    const childType = child?.version_details?.version_content?.type ?? child?.detail?.type ?? child?.type;
    return childType === t || (t === "canvas" && childType === "canva");
  };
  const getSectionStableId = (section) =>
    section?.tree_node_id ||
    section?.detail?.section_tree_node_id ||
    section?.version_details?.version_content?.section_tree_node_id ||
    section?.section_tree_node_id ||
    section?.section_id_ref ||
    "";
  const getStepStableId = (step) =>
    step?.tree_node_id ||
    step?.detail?.step_tree_node_id ||
    step?.version_details?.version_content?.step_tree_node_id ||
    step?.step_tree_node_id ||
    step?.step_id_ref ||
    "";
  const getSubstepStableId = (substep) =>
    substep?.tree_node_id ||
    substep?.detail?.sub_step_tree_node_id ||
    substep?.version_details?.version_content?.sub_step_tree_node_id ||
    substep?.sub_step_tree_node_id ||
    substep?.substep_tree_node_id ||
    substep?.substep_id_ref ||
    "";
  const getChildStableId = (child) => {
    const vc = child?.version_details?.version_content || {};
    const d = child?.detail || {};
    return (
      child?.tree_node_id ||
      vc.element_id ||
      vc.canvas_tree_node_id ||
      vc.canva_tree_node_id ||
      vc.rich_text_node_id ||
      vc.rich_text_tree_node_id ||
      vc.video_tree_node_id ||
      vc.attachment_tree_node_id ||
      vc.preview_tree_node_id ||
      d.element_id ||
      d.canvas_tree_node_id ||
      d.canva_tree_node_id ||
      d.rich_text_node_id ||
      d.rich_text_tree_node_id ||
      d.video_tree_node_id ||
      d.attachment_tree_node_id ||
      d.preview_tree_node_id ||
      ""
    );
  };

  data?.forEach((section, sectionIdx) => {
    const sectionStableId = getSectionStableId(section);
    section.steps?.forEach((step, stepIdx) => {
      const stepStableId = getStepStableId(step);
      step.children?.forEach((child, childIdx) => {
        if (childMatchesType(child, type)) {
          indices.push({
            sectionIdx,
            stepIdx,
            childIdx,
            sectionStableId,
            stepStableId,
            substepStableId: "",
            childStableId: getChildStableId(child),
            globalIndex: currentIndex++
          });
        }
      });

      step.substeps?.forEach((substep, substepIdx) => {
        const substepStableId = getSubstepStableId(substep);
        substep.children?.forEach((child, childIdx) => {
          if (childMatchesType(child, type)) {
            indices.push({
              sectionIdx,
              stepIdx,
              substepIdx,
              childIdx,
              sectionStableId,
              stepStableId,
              substepStableId,
              childStableId: getChildStableId(child),
              globalIndex: currentIndex++
            });
          }
        });
      });
    });
  });
  return indices;
}

/**
 * Get all node indices for different content types
 * @param {Array} contentData - Processed content data
 * @returns {Object} Object containing indices for each content type
 */
export function getAllNodeIndices(contentData) {
  return {
    richTextIndices: getNodeIndices(contentData, "rich-text"),
    videoIndices: getNodeIndices(contentData, "video"),
    formIndices: getNodeIndices(contentData, "form"),
    canvasIndices: getNodeIndices(contentData, "canvas"),
    attachmentIndices: getNodeIndices(contentData, "attachment")
  };
}

/**
 * Process content data to fix "canva" typo to "canvas" and handle section-level data (immutably)
 * Also merges parent_version_data when current version_content is missing essential details
 * @param {Array} data - Raw content data from API
 * @returns {Array} Processed content data with fixed types and proper section structure
 */
export function processContentData(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map((item) => {
    // Check if this is a section-level item (has section_name but no steps)
    if (item?.version_details?.version_content?.section_name && !item.steps) {
      // This is a section item, ensure it has the structure expected by Section component
      return {
        ...item,
        // Ensure section has steps array (even if empty) for compatibility
        steps: item.steps || []
      };
    }

    // This is a regular section with steps, process as before
    const processedSteps = item?.steps?.map((step) => {
      if (!step) return step;
      /** CREATE_STEP / save-step envelopes may include a draft row without type:"step" and without detail.step_tree_node_id */
      let s = step;
      const d = step.detail || {};
      const looksLikeStep =
        step.type === "step" ||
        (d.step_data != null && typeof d.step_data === "object") ||
        (d.step_data_prepared != null &&
          typeof d.step_data_prepared === "object") ||
        step.isDraft === true ||
        Boolean(step.step_id_ref) ||
        Boolean(d.step_tree_node_id);
      if (!step.type && looksLikeStep) {
        s = { ...step, type: "step" };
      }
      if (s.tree_node_id && !(s.detail && s.detail.step_tree_node_id)) {
        s = {
          ...s,
          detail: { ...(s.detail || {}), step_tree_node_id: s.tree_node_id },
        };
      }
      // Process substeps
      const processedSubsteps = s?.substeps?.map((substep) => {
        if (!substep) return substep;
        const processedChildren = substep?.children?.map((child) => {
          if (!child) return child;
          let processedChild = { ...child };

          // Fix canva typo (support both version_details and snapshot detail format)
          const childType = child?.version_details?.version_content?.type ?? child?.detail?.type ?? child?.type;
          if (childType === "canva") {
            const existingContent = processedChild.version_details?.version_content ?? processedChild.detail ?? {};
            processedChild = {
              ...processedChild,
              version_details: {
                ...processedChild.version_details,
                version_content: {
                  ...existingContent,
                  type: "canvas"
                }
              },
              detail: { ...(processedChild.detail || {}), type: "canvas" },
              type: "canvas",
            };
          }

          // Merge parent_version_data if current version_content is missing essential details
          if (child?.parent_version_data?.version_content) {
            const currentContent = processedChild.version_details?.version_content ?? processedChild.detail ?? {};
            const parentContent = child.parent_version_data.version_content;

            // Check if current content is missing essential details that exist in parent
            const needsMerge = (
              // For videos: missing video URL or name
              (currentContent.type === "video" && (!currentContent.video && !currentContent.video_http_url && !currentContent.video_url) && (parentContent.video || parentContent.video_http_url || parentContent.video_url)) ||
              // For rich-text: missing content
              (currentContent.type === "rich-text" && !currentContent.rich_text_content && parentContent.rich_text_content) ||
              // For canvas: missing pages
              (currentContent.type === "canvas" && !currentContent.pages && parentContent.pages) ||
              // For attachments: missing attachment data
              (currentContent.type === "attachment" && !currentContent.attachments && parentContent.attachments) ||
              // Missing name/title
              (!currentContent.name && !currentContent.pageTitle && !currentContent.elementTitle && (parentContent.name || parentContent.pageTitle || parentContent.elementTitle))
            );

            if (needsMerge) {
              processedChild = {
                ...processedChild,
                version_details: {
                  ...processedChild.version_details,
                  version_content: {
                    ...currentContent,
                    // Merge essential fields from parent_version_data
                    video: currentContent.video || parentContent.video,
                    video_http_url: currentContent.video_http_url || parentContent.video_http_url,
                    video_url: currentContent.video_url || parentContent.video_url,
                    rich_text_content: currentContent.rich_text_content || parentContent.rich_text_content,
                    pages: currentContent.pages || parentContent.pages,
                    attachments: currentContent.attachments || parentContent.attachments,
                    name: currentContent.name || parentContent.name,
                    pageTitle: currentContent.pageTitle || parentContent.pageTitle,
                    elementTitle: currentContent.elementTitle || parentContent.elementTitle,
                    short_description: currentContent.short_description || parentContent.short_description,
                  }
                }
              };
            }
          }

          return processedChild;
        });

        return {
          ...substep,
          children: processedChildren ?? substep?.children ?? []
        };
      });

      // Process step children
      const processedChildren = s?.children?.map((child) => {
        if (!child) return child;
        let processedChild = { ...child };

        // Fix canva typo (support both version_details and snapshot detail format)
        const childType = child?.version_details?.version_content?.type ?? child?.detail?.type ?? child?.type;
        if (childType === "canva") {
          const existingContent = processedChild.version_details?.version_content ?? processedChild.detail ?? {};
          processedChild = {
            ...processedChild,
            detail: { ...(processedChild.detail || {}), type: "canvas" },
            type: "canvas",
          };
          if (processedChild.version_details) {
            processedChild.version_details.version_content = { ...existingContent, type: "canvas" };
          } else {
            processedChild.version_details = { version_content: { ...existingContent, type: "canvas" } };
          }
        }

        // Merge parent_version_data if current version_content is missing essential details
        if (child?.parent_version_data?.version_content) {
          const currentContent = processedChild.version_details?.version_content ?? processedChild.detail ?? {};
          const parentContent = child.parent_version_data.version_content;

          // Check if current content is missing essential details that exist in parent
          const needsMerge = (
            // For videos: missing video URL or name
            (currentContent.type === "video" && (!currentContent.video && !currentContent.video_http_url && !currentContent.video_url) && (parentContent.video || parentContent.video_http_url || parentContent.video_url)) ||
            // For rich-text: missing content
            (currentContent.type === "rich-text" && !currentContent.rich_text_content && parentContent.rich_text_content) ||
            // For canvas: missing pages
            (currentContent.type === "canvas" && !currentContent.pages && parentContent.pages) ||
            // For attachments: missing attachment data
            (currentContent.type === "attachment" && !currentContent.attachments && parentContent.attachments) ||
            // Missing name/title
            (!currentContent.name && !currentContent.pageTitle && !currentContent.elementTitle && (parentContent.name || parentContent.pageTitle || parentContent.elementTitle))
          );

          if (needsMerge) {
            processedChild = {
              ...processedChild,
              version_details: {
                ...processedChild.version_details,
                version_content: {
                  ...currentContent,
                  // Merge essential fields from parent_version_data
                  video: currentContent.video || parentContent.video,
                  video_http_url: currentContent.video_http_url || parentContent.video_http_url,
                  video_url: currentContent.video_url || parentContent.video_url,
                  rich_text_content: currentContent.rich_text_content || parentContent.rich_text_content,
                  pages: currentContent.pages || parentContent.pages,
                  attachments: currentContent.attachments || parentContent.attachments,
                  name: currentContent.name || parentContent.name,
                  pageTitle: currentContent.pageTitle || parentContent.pageTitle,
                  elementTitle: currentContent.elementTitle || parentContent.elementTitle,
                  short_description: currentContent.short_description || parentContent.short_description,
                }
              }
            };
          }
        }

        return processedChild;
      });

      return {
        ...s,
        substeps: processedSubsteps ?? s?.substeps ?? [],
        children: processedChildren ?? s?.children ?? []
      };
    });

    return {
      ...item,
      steps: processedSteps ?? item?.steps ?? []
    };
  });
}

